import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {Router} from "@angular/router";
import {PfUserIndexResource} from "./resources/pfuserindexresource";
import {TranslateService} from './translate/translate.service';
import {
  ApiConnection,
  ErrorMessage,
  Interface,
  PfInterfaceResource,
  PfUserResource,
  PfAttendancecategoryResource,
  Attendancecategory,
  PfIndexResource
} from 'pfapi';
import {DomSanitizer} from "@angular/platform-browser";
import {AppSettings, WorkerFilterOption} from "./app-settings";
import {Subject, from, Observable} from "rxjs";
import {shareReplay, switchMap} from "rxjs/operators";

@Injectable()
export class AppService {
  protected interfaceResource: PfInterfaceResource;
  protected apiConnection: ApiConnection;
  public $notifyWorkersChanged = new Subject<void>();
  private indexEtag: string;
  protected connectError: string;
  protected offlineStatus = {offline: true};
  protected color = {theme: "dark"};
  private allWorkers: PfUserResource[] = [];
  private attendanceCategories: Map<Number, Attendancecategory> = new Map();
  private allWorkers$: Observable<PfUserResource[]> = this.$notifyWorkersChanged.pipe(
    switchMap(() => from([this.allWorkers])),
    shareReplay(1)
  );
  protected settings: AppSettings;
  public inputFocusIntervalTimer;
  protected oneTimeRunIsDone: boolean;
  protected terminalName: string;
  public defaultCicoType: string;
  protected terminalAttendanceCiCo: boolean;
  protected terminalAttendanceDetailed: boolean;
  protected terminalAttendanceAllowSimpleCiCo: boolean;
  protected audioError = new Audio('lib/audio/audioError.wav');
  private ciCoTriggeringWorker;
  private ciCoTriggeringWorkerTimeout: NodeJS.Timeout;
  protected workerInfoWorker;
  protected resizeIsOk;
  protected backgroundTiemeout;
  protected hostUrl;
  private imageObjectUrls = new Map<string, boolean>();

  constructor(private router: Router, private _translate: TranslateService, private locationx: Location, private sanitizer: DomSanitizer) {
    console.log('AppService Initialized...');
    this.reload24h();
    this.interfaceResource = new PfInterfaceResource(new Interface);
    this.interfaceResource.apiData.apiHost = this.getApiHost();
    this.apiConnection = new ApiConnection(this.interfaceResource);
    this.initializeBasicSettings();
    this.oneTimeRunIsDone = false;
    window.addEventListener('online', () => {
      this.offlineStatus.offline = false;
      this.lastStatus();
    });
    window.addEventListener('offline', () => {
      this.offlineStatus.offline = true;
      this.router.navigate(['/content']);
    });
    if (localStorage.getItem("t-theme")) {
      this.color.theme = localStorage.getItem("t-theme");
    }
    if (JSON.parse(localStorage.getItem("t-settings"))) {
      this.settings = JSON.parse(localStorage.getItem("t-settings"));
    }
    this.checkOnline();
  }

  getApiHost() {
    let returnedApiHost = null;
    if (this.hostUrl == null || this.hostUrl == '') {
      let host = document.location.host;
      if (host === "www.projectfacts.de") {
        host = "sync.projectfacts.de";
      }
      returnedApiHost = document.location.protocol + "//" + host + "/api";
    } else {
      returnedApiHost = this.hostUrl + "/api";
    }
    console.log('go to ApiHost: ' + returnedApiHost);
    return returnedApiHost;
  }

  setHostUrl(hostUrl) {
    this.hostUrl = hostUrl;
  }

  getRouter() {
    return this.router;
  }

  getInterfaceName() {
    return this.terminalName;
  }

  getInterfaceAttendanceCiCo() {
    return this.terminalAttendanceCiCo;
  }

  getInterfaceAttendanceDetailed() {
    return this.terminalAttendanceDetailed;
  }

  getInterfaceAttendanceAllowSimpleCiCo() {
    return this.terminalAttendanceAllowSimpleCiCo;
  }

  getId() {
    return this.interfaceResource.apiData._id;
  }

  setId(id: number) {
    this.interfaceResource.apiData._id = id;
  }

  getPw() {
    return this.interfaceResource.apiData.password;
  }

  setPw(pw: string) {
    this.interfaceResource.apiData.password = pw;
  }

  getApiConnection() {
    return this.apiConnection;
  }

  setApiConnection(apiConnection: ApiConnection) {
    this.apiConnection = apiConnection;
  }

  setInterfaceResource(interfaceResource: PfInterfaceResource) {
    this.interfaceResource = interfaceResource;
  }

  getOfflineStatus() {
    return this.offlineStatus;
  }

  setConnectError(connectError: string) {
    this.connectError = connectError;
  }

  getColor() {
    return this.color;
  }

  setColor(theme: string) {
    this.color.theme = theme;
    localStorage.setItem("t-theme", this.color.theme);
  }

  getAllWorkers(): PfUserResource[] {
    return this.allWorkers;
  }

  getAllWorkersObservable(): Observable<PfUserResource[]> {
    return this.allWorkers$;
  }

  resetAllWorkers() {
    this.allWorkers = [];
    this.$notifyWorkersChanged.next();
  }

  getAttendanceCategories(): Map<Number, Attendancecategory> {
    return this.attendanceCategories;
  }

  initializeBasicSettings() {
    this.settings = {
      workerFilter: WorkerFilterOption.Default,
      ciCoDetails: true,
      columnCount: 6,
      littleMessageBlock: false,
      showImages: false,
      showAbsent: false,
      showWorkerBackground: false
    };
  }

  saveSettings() {
    localStorage.setItem("t-settings", JSON.stringify(this.settings));
  }

  getSettings(): AppSettings {
    return this.settings;
  }

  clearInputFocusIntervalTimer() {
    clearInterval(this.inputFocusIntervalTimer);
  }

  getAudioError() {
    return this.audioError;
  }


  setCiCoTriggeringWorker(worker: PfUserResource) {
    this.ciCoTriggeringWorker = worker;
    if (this.ciCoTriggeringWorkerTimeout) {
      clearTimeout(this.ciCoTriggeringWorkerTimeout);
    }

    this.ciCoTriggeringWorkerTimeout = setTimeout(() => {
      this.ciCoTriggeringWorker = null;
      this.ciCoTriggeringWorkerTimeout = null;
    }, 4002);
  }

  getCiCoTriggeringWorker() {
    return this.ciCoTriggeringWorker;
  }

  setWorkerInfoWorker(worker) {
    this.workerInfoWorker = worker;
  }

  getWorkerInfoWorker() {
    return this.workerInfoWorker;
  }

  setResizeIsOk(bool: boolean) {
    this.resizeIsOk = bool;
  }

  selectLang(lang: string) {
    // set current lang;
    this._translate.use(lang);
  }

  public reload24h() {
    setTimeout(() => {
      document.location.reload();
    }, 3600000);
  }

  public checkOnline() {
    if (navigator.onLine == true) {
      this.offlineStatus.offline = false;
      this.goTerminal();
    } else {
      this.offlineStatus.offline = true;
      this.router.navigate(['/content']);
    }
  }

  public lastStatus() {
    this.goTerminal();
    (<HTMLInputElement>document.getElementById("rfidInput")).value = null;
  }

  public millisec2Minutes(millisec: number) {
    return Math.floor(millisec / 60000);
  }

  public minutesToHoursAndMin(minutes: number) {
    let hours: number = Math.floor(minutes / 60);
    let minutesOut: number = minutes - (hours * 60);
    return String(hours) + "h " + (minutesOut < 10 ? '0' : '') + minutesOut;
  }

  public goTerminal() {
    let loadedInterface: Interface = JSON.parse(localStorage.getItem("t-dataInterface"));
    if (loadedInterface) {                                              // Wenn wir im lokal storage ein Interface gespeichert haben
      this.connectError = "WRONG_DATA_AT_STORAGE";// Fallunterscheidung um später den richtigen Error bei Error register vom interface aus zu geben
      let loadedPassword: string = localStorage.getItem("t-dataPassword");
      if (!loadedPassword) {
        localStorage.setItem("t-dataPassword", loadedInterface.password); // Speichern des Passwortes im lokalen Speicher
      }
      this.interfaceResource = new PfInterfaceResource(loadedInterface);
      this.loadTerminal(true);                                              // wir haben ein Interface im lokalen Speicher -> Direckt laden
    } else {
      this.connectError = "WRONG_DATA_INPUT";           // Fallunterscheidung um später den richtigen Error bei Error register vom interface aus zu geben
      this.router.navigate(['/connect']);                               // wir haben KEIN Interface im lokalen Speicher -> Daten erfassen
    }
  }

  public loadTerminal(loadFromStorage: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      if (loadFromStorage == false) {
        localStorage.setItem("t-dataInterface", JSON.stringify(this.interfaceResource.apiData));         // Speichern des neuen Interfaces
        localStorage.setItem("t-dataPassword", this.interfaceResource.apiData.password);                 // Speichern des Passwortes
      }
      this.apiConnection.connect(this.interfaceResource).then(() => {
          this.interfaceResource.apiData.password = localStorage.getItem("t-dataPassword");            // laden des lokal gespeicherten Passwortes
          return this.interfaceResource.load(this.apiConnection);
      }).then((interfaceR) => {
        console.log("Load successful: ", interfaceR.apiData);
        this.selectLang(interfaceR.apiData.language.value); // Aus Pf Einstellungen für Interface direkt sprache übergeben.
        this.terminalName = interfaceR.apiData.name;
        this.defaultCicoType = interfaceR.apiData.attendanceCategory.value;
        this.terminalAttendanceCiCo = interfaceR.apiData.attendanceCiCo;
        this.terminalAttendanceDetailed = interfaceR.apiData.attendanceDetailed;
        this.terminalAttendanceAllowSimpleCiCo = interfaceR.apiData.attendanceAllowSimpleCiCo;
        this.interfaceResource.apiData.password = localStorage.getItem("t-dataPassword");

        const indexResource = new PfIndexResource<Attendancecategory>(new PfAttendancecategoryResource(new Attendancecategory()).getBaseUrl());
        indexResource.loadWithchilds(this.apiConnection, PfAttendancecategoryResource, true).then(index => {
          for (const i of index.childs) {
            this.attendanceCategories.set(i.apiData._id, i.apiData);
          }

          this.pollingChilds();
          this.router.navigate(['/content']);
        });

      }).catch((err: ErrorMessage) => {
          console.log("catched: " + err.message);
          this.connectError = err.message;

          this.interfaceResource = new PfInterfaceResource(JSON.parse(localStorage.getItem("t-dataInterface")));
          this.apiConnection = new ApiConnection(this.interfaceResource);
          this.router.navigate(['/connect']);
          reject("CONNECTION_FAILED" + " - " + this.connectError);
        });
    });
  }

  public pollingChilds(init = true) {                                                    // Die Arbeiter auf aktuellem stand halten
    new PfUserIndexResource().loadWithchilds(this.apiConnection)                     // true == er wartet bis alle childs geladen sind dann .then
      .then((loadedIndex) => {
        const newIndexEtag = loadedIndex.apiData._etag.value;
        if (!this.indexEtag || this.indexEtag !== newIndexEtag) {
          this.indexEtag = newIndexEtag;
          this.indexEtag = newIndexEtag;

          this.replaceChangedWorkers(loadedIndex.childs as PfUserResource[]);
          this.loadImages();
          this.$notifyWorkersChanged.next();
        }
      });
    if (init) {
      setInterval(async () => {
        this.pollingChilds(false);
      }, 20000);
    }
  }

  private replaceChangedWorkers(newWorkers: PfUserResource[]) {
    const oldWorkers = this.allWorkers;
    this.allWorkers = newWorkers;

    const oldWorkersMap: Map<number, PfUserResource> = new Map();
    for (const oldWorker of oldWorkers) {
      oldWorkersMap.set(oldWorker.apiData._id,  oldWorker);
    }

    // Die neuen Worker gewinnen am Anfang. Wir ersetzen aber alle unveränderen Worker mit den alten, damit z.B. die
    // Bilder nicht neu geladen werden müssen und die Ansicht stabiler bleibt
    for (let i = 0; i < this.allWorkers.length; i++) {
      const newWorker = this.allWorkers[i];
      const oldWorker = oldWorkersMap.get(newWorker.apiData._id);
      if (oldWorker && oldWorker.apiData._lastModifiedDate === newWorker.apiData._lastModifiedDate) {
        this.allWorkers[i] = oldWorker;
      }
      this.allWorkers[i].apiData["stateA"] = this.allWorkers[i].apiData.checkedIn ? "checkedIn" : "checkedOut";
    }
  }

  private loadImages() {
    const oldObjectUrls = new Map(this.imageObjectUrls);
    this.imageObjectUrls.clear();

    const promises = [];
    for (const worker of this.allWorkers) {
      if (!worker.userImage) {
        promises.push(worker.loadUserImage(this.apiConnection).then(image => this.makeSafeUrl(image)));
        promises.push(worker.loadUserBackgroundImage(this.apiConnection).then(image => this.makeSafeUrl(image)));
      } else {
        if (worker.userImage.src) {
          this.imageObjectUrls.set(worker.userImage.src, true);
        }
        if (worker.userBackground.src) {
          this.imageObjectUrls.set(worker.userBackground.src, true);
        }
      }
    }

    Promise.all(promises).then(() => {
      this.revokeSafeUrls(oldObjectUrls);
    });
  }

  private makeSafeUrl(image) {
    if (image && image.src) {
      this.imageObjectUrls.set(image.src, true);
      const sani = this.sanitizer;
      Object.defineProperty(
        image,
        'safeSrc',
        {
          get: function() {
            if (!this._safeSrc) {
              this._safeSrc = sani.bypassSecurityTrustUrl(image.src);
            }
            return this._safeSrc;
          }
        }
      );
    }
  }

  private revokeSafeUrls(urls2revoke: Map<string, boolean>) {
    urls2revoke.forEach((value, key) => {
      if (!this.imageObjectUrls.has(key)) {
        window.URL.revokeObjectURL(key);
      }
    });
  }

  public reconnect(jumpTo: boolean) {
    this.interfaceResource = new PfInterfaceResource(new Interface);
    this.interfaceResource.apiData.apiHost = this.getApiHost();
    this.apiConnection = new ApiConnection(this.interfaceResource);
    if (jumpTo == true) {
      this.checkOnline();
    }
  }

  public isVisibleWorker(worker: PfUserResource): boolean {
    if (worker.apiData.absent) {
      return false;
    } else if (!this.settings.workerFilter || !this.getInterfaceAttendanceDetailed()) {
      return worker.apiData.checkedIn == true && worker.apiData.currentcicotyp.value == this.defaultCicoType;
    } else if (this.settings.workerFilter == WorkerFilterOption.CheckedIn) {
      return worker.apiData.checkedIn == true;
    } else {
      return true;
    }
  }

  public makeBackgroundTimeout(triggeringWorker) {
    setTimeout(() => {
      const workerBackgroundElement = document.getElementById("workerBackground");
      if (this.getSettings().showWorkerBackground && workerBackgroundElement) {
        if (triggeringWorker.userBackground && triggeringWorker.userBackground.src) {      // Wir prüfen ob ein hintergrundbild vorhanden ist
          workerBackgroundElement.classList.add("workerBackgroundShow");
          clearTimeout(this.backgroundTiemeout);
          this.backgroundTiemeout = setTimeout(() => {
            if (this.locationx.path() == '/content') {       // Wir fangen den Fehler ab, in anderem Route versuchen wir auf das Element workerBackground zuzugreifen, obwohl es nicht mehr existiert
              workerBackgroundElement.classList.remove("workerBackgroundShow");
            }
          }, 4001);
        } else {
          clearTimeout(this.backgroundTiemeout);
          if (this.locationx.path() == '/content') {       // Wir fangen den Fehler ab, in anderem Route versuchen wir auf das Element workerBackground zuzugreifen, obwohl es nicht mehr existiert
            workerBackgroundElement.classList.remove("workerBackgroundShow");
          }
        }
      }
    }, 100);
  }

  public ciCoDetailsTrigger() {
    if (this.settings.ciCoDetails == true) {
      document.getElementById("workers").classList.add("workersShow");
      document.getElementById("absentWorkers").classList.add("workersShow");
      document.getElementById("timeBlock").classList.add("timeBlockWithWorkers");
      document.getElementById("dateSmall").classList.add("dateSmallWithWorkers");
      document.getElementById("messageBlock").classList.add("messageBlockWithWorkers");
      document.getElementById("subTextMessage").classList.add("subTextMessageWithWorkers");
      this.setResizeIsOk(true);
      // this.addEventListenerResize();   // der event listener ist immer im hintergrund am laufen
    } else {
      document.getElementById("workers").classList.remove("workersShow");
      document.getElementById("absentWorkers").classList.remove("workersShow");
      document.getElementById("timeBlock").classList.remove("timeBlockWithWorkers");
      document.getElementById("dateSmall").classList.remove("dateSmallWithWorkers");
      document.getElementById("messageBlock").classList.remove("messageBlockWithWorkers");
      document.getElementById("subTextMessage").classList.remove("subTextMessageWithWorkers");
      this.setResizeIsOk(false);
      // window.removeEventListener("resize", () => {     // der event listener ist immer im hintergrund am laufen
      //   this.checkWorkerComponentChanges()
      // });
    }
  }

  public littleMessageBlockTrigger() {
    if (this.settings.littleMessageBlock == true) {
      document.getElementById("timeBlock").classList.add("timeBlockLittleMessageBlock");
      document.getElementById("dateSmall").classList.add("dateSmallLittleMessageBlock");
      document.getElementById("messageBlock").classList.add("messageBlockLittleMessageBlock");
      document.getElementById("subTextMessage").classList.add("subTextMessageLittleMessageBlock");
      document.getElementById("workers").classList.add("workersLittleMessageBlock");
      document.getElementById("absentWorkers").classList.add("workersLittleMessageBlock");
    } else {
      document.getElementById("timeBlock").classList.remove("timeBlockLittleMessageBlock");
      document.getElementById("dateSmall").classList.remove("dateSmallLittleMessageBlock");
      document.getElementById("messageBlock").classList.remove("messageBlockLittleMessageBlock");
      document.getElementById("subTextMessage").classList.remove("subTextMessageLittleMessageBlock");
      document.getElementById("workers").classList.remove("workersLittleMessageBlock");
      document.getElementById("absentWorkers").classList.remove("workersLittleMessageBlock");
    }
  }

  //---Überlauf Arrays mit maximaler Größe, bedeutet das es mehrere überlauf Arrays geben kann---
  /*
   public changeWorkerComponent(rows: number, columns: number) {
   this.oneTimeRunIsDone = false;
   console.log("anpassung notwendig");
   let workerGroups = [];
   workerGroups[0] = this.appService.getChilds()[0];                                                 // in dieses array werden alle worker geschrieben; Dieses Array ist ein Duplikat von IndexResource.childs
   let groupsCounter = 1 + Math.floor(((workerGroups[0].length / (rows * columns)) - 0.000000009));  // wir bestimmern wieviele arrays wir brauchen
   let workerGroupsHelp = [];
   for (let j = 0; j < groupsCounter; j++) {                                                         // für jedes Array das wir füllen wollen
   workerGroupsHelp[j] = [];
   for (let i = 0; i < rows * columns; i++) {                                                      // wir gestallten arrays in der länge wieviele Kacheln auf eine seite sollen rows*4
   if (workerGroups[0][j * (rows * columns) + i]) {
   workerGroupsHelp[j][i] = workerGroups[0][j * (rows * columns) + i];                         // wir übertragen in die neuen arrays
   }
   }
   }
   this.allWorkers.workerGroups = workerGroupsHelp;
   this.allWorkers.columns = columns;
   this.appService.setAllWorkers(this.allWorkers);                                                   // wir übergeben unser Ergebniss
   }
   */


  public getCiCoIcon(currentcicotype) {
    return currentcicotype && currentcicotype._apiData.currentcicotyp
      ? this.getAttendanceCategories().get(currentcicotype._apiData.currentcicotyp.value).icon
      : "";
  }

  public getCiCoIconColor(currentcicotype) {
    return '#' + (currentcicotype && currentcicotype._apiData.currentcicotyp
      ? this.getAttendanceCategories().get(currentcicotype._apiData.currentcicotyp.value).color
      : "FFFFFF");
  }

  public getCiCoIconTitle(currentcicotype) {
    return currentcicotype && currentcicotype._apiData.currentcicotyp
      ? this.getAttendanceCategories().get(currentcicotype._apiData.currentcicotyp.value).name
      : "";
  }

}
